<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { FairIcon } from '@/ui/primitives/FairIcon'
import type { FairIconName } from '@/ui/tokens/icons.data'
import type {
  FairInputTextPreviewState,
  FairInputTextVariant,
} from '@/ui/tokens/inputs.data'

interface FairInputTextProps {
  modelValue?: string
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  rounded?: boolean
  focused?: boolean
  variant?: FairInputTextVariant
  helperText?: string
  showHelper?: boolean
  badge?: string | number
  leftIcon?: FairIconName
  rightIcon?: FairIconName
  showClearButton?: boolean
  combobox?: boolean
  inputId?: string
  previewState?: FairInputTextPreviewState
  interactive?: boolean
}

const props = withDefaults(defineProps<FairInputTextProps>(), {
  modelValue: '',
  placeholder: 'Placeholder',
  disabled: false,
  invalid: false,
  rounded: false,
  focused: false,
  variant: 'outlined',
  helperText: 'Helper Text',
  showHelper: false,
  badge: undefined,
  showClearButton: false,
  combobox: false,
  previewState: undefined,
  interactive: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
  input: [event: Event]
  clear: []
}>()

const slots = useSlots()
const hasCustomContent = computed(() => Boolean(slots.default))

const isDisabled = computed(() =>
  props.disabled || props.previewState === 'disabled',
)

const isFocused = computed(() =>
  props.focused || props.previewState === 'active',
)

const rootClass = computed(() => ({
  'fair-input-text': true,
  'fair-input-text--invalid': props.invalid,
  'fair-input-text--non-interactive': !props.interactive,
  [`fair-input-text--preview-${props.previewState}`]: Boolean(props.previewState),
}))

const fieldClass = computed(() => ({
  'fair-input-text__field': true,
  'fair-input-text__field--rounded': props.rounded,
  'fair-input-text__field--focused': isFocused.value,
  'fair-input-text__field--invalid': props.invalid,
  'fair-input-text__field--disabled': isDisabled.value,
  'fair-input-text__field--flat': props.variant === 'flat',
  'fair-input-text__field--underline': props.variant === 'underline',
}))

const inputIconColor = computed(() =>
  props.variant === 'underline'
    ? 'currentColor'
    : 'var(--fair-color-primary-blue-100)',
)

function resolveFieldIcon(name: FairIconName): FairIconName {
  if (name === 'search') {
    return 'search-field'
  }

  if (name === 'close') {
    return 'close-field'
  }

  return name
}

function iconSlotClass(name: FairIconName): Record<string, boolean> {
  return {
    'fair-input-text__icon-slot--search': name === 'search',
    'fair-input-text__icon-slot--close': name === 'close',
  }
}

function updateValue(event: Event) {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.value)
  emit('input', event)
}

function handleClear() {
  if (!props.interactive || isDisabled.value) {
    return
  }

  emit('update:modelValue', '')
  emit('clear')
}
</script>

<template>
  <div :class="rootClass">
    <div :class="fieldClass">
      <span
        v-if="leftIcon"
        class="fair-input-text__icon-slot"
        :class="iconSlotClass(leftIcon)"
      >
        <span class="fair-input-text__icon-graphic">
          <FairIcon
            :name="resolveFieldIcon(leftIcon)"
            :color="inputIconColor"
          />
        </span>
      </span>

      <div class="fair-input-text__content">
        <slot>
          <input
            :id="inputId"
            class="fair-input-text__control type-body-ui"
            type="text"
            :role="combobox ? 'combobox' : undefined"
            :aria-autocomplete="combobox ? 'list' : undefined"
            :aria-expanded="combobox ? focused : undefined"
            :value="modelValue"
            :placeholder="placeholder"
            :disabled="isDisabled"
            @input="updateValue"
            @focus="emit('focus', $event)"
            @blur="emit('blur', $event)"
          >
        </slot>

        <span
          v-if="badge !== undefined && badge !== ''"
          class="fair-input-text__badge type-caption"
        >
          {{ badge }}
        </span>
      </div>

      <button
        v-if="showClearButton && modelValue && !hasCustomContent"
        type="button"
        class="fair-input-text__clear"
        aria-label="Clear"
        :disabled="isDisabled"
        @click="handleClear"
      >
        <span
          class="fair-input-text__icon-slot"
          :class="props.variant === 'underline'
            ? 'fair-input-text__icon-slot--close-small'
            : 'fair-input-text__icon-slot--close'"
        >
          <span class="fair-input-text__icon-graphic">
            <FairIcon
              :name="props.variant === 'underline' ? 'close-small' : 'close-field'"
              :color="inputIconColor"
            />
          </span>
        </span>
      </button>

      <span
        v-if="rightIcon"
        class="fair-input-text__icon-slot"
        :class="iconSlotClass(rightIcon)"
      >
        <span class="fair-input-text__icon-graphic">
          <FairIcon
            :name="resolveFieldIcon(rightIcon)"
            :color="inputIconColor"
          />
        </span>
      </span>
    </div>

    <p
      v-if="showHelper"
      class="fair-input-text__helper type-caption"
    >
      {{ helperText }}
    </p>
  </div>
</template>
